import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import TitleForms from '../../forms/GenericBaseForms/TitleForms/TitleForms';
import './FAQComponent.scss';
import { GoBackButtonComponent } from '../../UI/GoBackButtonComponent/GoBackButtonComponent';
import { useLocation, useNavigate } from 'react-router';
import { ScreenType } from '../../../common/view-model';
import { RootState } from '../../../redux/AppReducer';
import { useSelector } from 'react-redux';

interface IFAQElement {
	question: string;
	answer: string | ReactNode;
}

const FAQElement: React.FC<IFAQElement> = ({ question, answer }) => {
	const [isOpen, setOpen] = useState<boolean>(false);
	return (
		<div className='faq-container'>
			<div className='title-faq'>
				<div className={`text-title ${isOpen ? 'active' : ''}`}>{question}</div>
				<i
					className={isOpen ? 'ri-subtract-line' : 'ri-add-line'}
					onClick={() => setOpen(!isOpen)}
				></i>
			</div>
			{isOpen && <div className='description-faq'>{answer}</div>}
		</div>
	);
};

interface IFAQSelected {
	collapsed: boolean;
}

interface IFAQProps {
	isGeneral?: boolean;
}
const FAQComponent: React.FC<IFAQProps> = ({ isGeneral = false }) => {
	const navigate = useNavigate();
	const FAQsArray = isGeneral
		? [
				{
					sectionTitle: 'New Hope Network Initiatives',
					questions: [
						{
							id: 1,
							question: 'Why is transparency important at Natural Products Expo?',
							answer: (
								<p>
									With the growing diversity of natural products and ongoing innovation across
									categories, clear communication about ingredients, sourcing, and manufacturing
									processes is essential. To maintain these high standards, every brand undergoes a
									thorough review by New Hope's Standards team before the show. This pre-show
									evaluation ensures all products meet our established criteria and helps brands
									showcase their commitment to transparency, supporting informed decision-making for
									buyers and maintaining the integrity of the natural products marketplace.
								</p>
							)
						},
						{
							id: 2,
							question: 'How does Beacon Discovery help support our Transparency Initiative?',
							answer: (
								<>
									<p>
										We aim to make an industry‑wide impact on climate change while giving brands the
										knowledge and tools to make affordable improvements in sustainability and
										transparency, so they can stand out for doing the right thing. Beacon was built
										to make discovery faster, more reliable and more transparent using the metrics
										that matter.
									</p>
									<p>
										Learn more in our article{' '}
										<a
											href='https://www.newhope.com/manufacturing-and-supply/navigating-integrity-natural-products-cpg-industry'
											target='_blank'
											rel='noreferrer'
										>
											Navigating Integrity in the Natural Products CPG Industry
										</a>
										.
									</p>
								</>
							)
						},
						{
							id: 3,
							question: 'How does MarketReady Verified work?',
							answer: (
								<>
									<p>
										When natural products businesses enter the New Hope ecosystem, we perform a
										labeling review to ensure ingredients and claims meet our standards. Some brands
										make corrections themselves; others opt for affordable, accessible regulatory
										assistance to bring labeling into both event and federal compliance. Products
										with the MarketReady Verified banner allow retailers to be confident that
										labeling has been reviewed and revised for FDA labeling compliance.
									</p>
									<p>
										{' '}
										<a href='https://marketreadyinsights.com/' target='_blank' rel='noreferrer'>
											Learn more here
										</a>
										.
									</p>
								</>
							)
						}
					],
					id: 'network-initiative'
				},
				{
					sectionTitle: 'About Beacon Discovery',
					questions: [
						{
							id: 4,
							question: 'What is Beacon Discovery?',
							answer: (
								<p>
									Beacon Discovery helps buyers navigate the many booths at Expo. Before the show,
									buyers can identify which products they want to see and find brands quickly
									on‑floor. Brands upload ingredients, values, mission and more; Beacon then matches
									them with retailer searches using SPINS categories and data analytics. It’s now
									available year‑round—365 days a year—for retailers to search, even for brands not
									exhibiting at Expo.
								</p>
							)
						},
						{
							id: 5,
							question: 'How is Beacon Discovery different from a directory?',
							answer: (
								<p>
									Beacon is much more than a directory. It provides, in one place, a suite of new
									metrics and tools to find products that meet practical, value‑driven, and
									mission‑oriented objectives: avoiding prohibited ingredients, sustainability
									impact estimates, processing transparency, regulatory compliance, distributor and
									broker details, high‑level velocity and sales metrics, plus robust filtering with
									over 100 categories aligned with SPINS’ shelf category architecture.
								</p>
							)
						},
						{
							id: 6,
							question: 'How is Beacon Discovery different from other product discovery platforms?',
							answer: (
								<p>
									Beacon matches brands with buyers based on New Hope Network’s standards and the
									transparency priorities retailers want. It identifies connections by ingredient
									preferences, values, mission, and over 100 other filters—including a
									sustainability impact estimate from HowGood—then lets buyers reach out directly.
									Beacon also integrates with the Natural Products Expo West & Newtopia Now
									experience, making it easier to find brands on‑floor.
								</p>
							)
						},
						{
							id: 7,
							question: 'Who will be seen on Beacon Discovery?',
							answer: (
								<p>
									Our current version features CPG brands that conform to Natural Products Expo
									standards (whether or not they are exhibiting). As of February 2025, Business
									Providers are also eligible for profiles. On the discovery side, retailers,
									ingredient suppliers, investors, distributors and other Expo attendees can search
									for brands and products.
								</p>
							)
						},
						{
							id: 8,
							question: 'What are the banned ingredient templates based on?',
							answer: (
								<>
									<p>
										Banned ingredient templates are part of what makes Beacon unique. Choose a
										template that aligns with your store’s standards and Beacon will filter out any
										products containing those ingredients.
									</p>
									<p>
										<strong>Ingredient template options:</strong>
										<br />
										• New Hope Network Standards (default)
										<br />• Retailer Standards and Retailer Standards Plus (more rigorous, based on
										public lists from leading retailers and our Standards team’s expertise)
									</p>
									<p>
										Ingredients marked with “*” are banned only if genetically engineered. If a
										product contains such an ingredient and lacks Non‑GMO and/or Organic
										certification, Beacon will not display it.
									</p>
									<p>
										<strong>Exceptions:</strong>
										<br />
										• Nail polish may contain artificial colors in all templates.
										<br />
										• Hair color may contain Paraphenylenediamine (PPD) in the New Hope Network
										Standards and Retailer Standards templates.
										<br />• Products in Home and Miscellaneous categories are not filtered against
										banned templates.
									</p>
									<p>
										The number of ingredients banned per template reflects every way an ingredient
										name may appear on a label—including alternate spellings, abbreviations, or
										grouped names (e.g. PEGs).
									</p>
								</>
							)
						},
						{
							id: 9,
							question:
								'Why am I seeing products labeled ‘Not for sale in US’ on Beacon Discovery? ',
							answer: (
								<p>
									New Hope Network allows these brands to exhibit at shows and on Beacon to foster
									innovation and discovery, balanced with labeling compliance. International brands
									get a chance to see how the US market responds to their products, while US buyers
									see what’s available internationally in a market setting where international
									products and ingredients are increasingly popular. Many of these brands do meet
									labeling compliance within their home jurisdictions.
								</p>
							)
						}
					],
					id: 'about-beacon'
				},
				{
					sectionTitle: 'How to get on Beacon Discovery',
					questions: [
						{
							id: 9,
							question: 'How do I upload products to Beacon?',
							answer: (
								<p>
									Onboarding to Beacon Discovery begins by uploading your products into the New Hope
									Product Portal, powered by SPINS. Once uploaded, our New Hope Standards team will
									review your products for approval and placement on Beacon Discovery. To get
									started, reach out to{' '}
									<a href='mailto:AudienceSuccessTeam@NewHope.com' target='_blank' rel='noreferrer'>
										AudienceSuccessTeam@NewHope.com
									</a>
									.
								</p>
							)
						},
						{
							id: 10,
							question: 'How much does Beacon Discovery cost?',
							answer: (
								<p>
									Beacon is included in all booth contracts for Expo West and Newtopia Now—there’s
									no additional cost to exhibiting brands, and onboarding brands can upload
									unlimited products at no extra charge. For non‑exhibiting brands, the cost is
									$2,000 per year.
								</p>
							)
						},
						{
							id: 11,
							question: 'How long will products be visible on Beacon Discovery?',
							answer: (
								<p>
									Exhibitor inclusions run January through December each year. Non‑exhibitor
									subscriptions are annual, beginning on the subscription date and ending one year
									later.
								</p>
							)
						}
					],
					id: 'get-on-beacon'
				},
				{
					sectionTitle: 'Beacon Discovery partners',
					questions: [
						{
							id: 12,
							question: 'What is a HowGood estimate?',
							answer: (
								<>
									<p>
										Brands on Beacon automatically receive a sustainability impact estimate from
										HowGood, the world’s largest sustainable ingredient database. Retailers value
										this transparency as a major differentiator when choosing products. Once you
										receive your estimate, you can promote it on Beacon.
									</p>
									<p>
										<a href='https://www.howgood.com/new-hope' target='_blank' rel='noreferrer'>
											Learn more here
										</a>
										.
									</p>
								</>
							)
						},
						{
							id: 13,
							question: 'What is SPINS?',
							answer: (
								<p>
									SPINS is a leading wellness‑focused data company and passionate advocate for
									natural and organic retailers and brands. Their technology powers product
									intelligence in Beacon Discovery, the New Hope Product Portal at events, and all
									filtering on the platform.
								</p>
							)
						}
					],
					id: 'beacon-partners'
				},
				{
					sectionTitle: 'Using the Beacon Hub',
					questions: [
						{
							id: 14,
							question: 'What is the Beacon Hub?',
							answer: (
								<p>
									The Beacon Hub allows brands to view and edit most of their product and brand data
									year‑round. Keeping your data current helps retailers discover your products and
									reach out directly when they’re interested.
								</p>
							)
						},
						{
							id: 15,
							question:
								'Who has access to the Beacon Hub and what if I don’t see it in my profile?',
							answer: (
								<p>
									The person whose name and email are associated with your New Hope Standards
									application has access. Create an account on{' '}
									<a href='https://beacon.newhope.com' target='_blank' rel='noreferrer'>
										beacon.newhope.com
									</a>{' '}
									using that same email, and you’ll see “My Beacon Hub” in your profile. If you
									still don’t have access, please reach out to{' '}
									<a href='mailto:AudienceSuccessTeam@NewHope.com' target='_blank' rel='noreferrer'>
										AudienceSuccessTeam@NewHope.com
									</a>
									.
								</p>
							)
						},
						{
							id: 16,
							question: 'Some fields are not editable in the Beacon Hub, how can I update them?',
							answer: (
								<p>
									Certain fields in the Brand and Product Info sections are not yet editable by
									users. We’re working toward full self‑service editing, but in the meantime, to
									make additional changes please email{' '}
									<a href='mailto:AudienceSuccessTeam@NewHope.com' target='_blank' rel='noreferrer'>
										AudienceSuccessTeam@NewHope.com
									</a>
									.
								</p>
							)
						},
						{
							id: 17,
							question: 'What is a HowGood estimate and why should I show it on my products?',
							answer: (
								<>
									<p>
										As a Beacon brand, you automatically receive an affordable sustainability impact
										estimate from HowGood, giving your products transparency that retailers and
										consumers demand. This can be a major differentiator when buyers choose what to
										stock.
									</p>
									<p>
										HowGood is an independent research company with the world’s largest food product
										sustainability database. Drawing from over 600 data sources and certifications,
										they help leading brands and retailers measure and improve environmental and
										social impact. Note: estimates currently cover only food and beverage products.
									</p>
									<p>
										The HowGood Sustainability Impact Estimate measures overall product
										sustainability across eight key metrics: Greenhouse Gas Emissions, Soil Health,
										Water Usage, Land Use, Biodiversity, Animal Welfare, Labor Risk, and Processing.
										Scores on Beacon range from negative to positive impact:
									</p>
									<p>
										<strong>Good</strong> = better than 70% of assessed products
										<br />
										<strong>Great</strong> = better than 85%
										<br />
										<strong>Best</strong> = better than 95%
									</p>
									<p>
										<a href='https://www.howgood.com/new-hope' target='_blank' rel='noreferrer'>
											Learn more here
										</a>
										.
									</p>
								</>
							)
						}
						// {
						// section commented because of ticket BP-1891-hide question Violation and improvement
						// 	id: 18,
						// 	question:
						// 		'In the Product Info section, what is the difference between a Violation and an Improvement?',
						// 	answer: (
						// 		<>
						// 			<p>
						// 				A <strong>Violation</strong> indicates corrective action is required for a
						// 				product to be eligible on Beacon or for an upcoming Expo. An{' '}
						// 				<strong>Improvement</strong> is a suggestion from our Standards Specialists to
						// 				enhance compliance; it won’t prevent your product from displaying on Beacon or
						// 				at Expo.
						// 			</p>
						// 			<p>
						// 				To provide additional information or ask questions, contact your Standards
						// 				Specialist at{' '}
						// 				<a href='mailto:standards@newhope.com' target='_blank' rel='noreferrer'>
						// 					standards@newhope.com
						// 				</a>
						// 				. If you’d like full compliance assistance, visit{' '}
						// 				<a href='https://marketreadyinsights.com/' target='_blank' rel='noreferrer'>
						// 					MarketReady Insights
						// 				</a>
						// 				.
						// 			</p>
						// 		</>
						// 	)
						// }
					],
					id: 'beacon-hub-faq'
				}
		  ]
		: [
				{
					sectionTitle: 'Using the Beacon Hub',
					questions: [
						{
							id: 14,
							question: 'What is the Beacon Hub?',
							answer: (
								<p>
									The Beacon Hub allows brands to view and edit most of their product and brand data
									year‑round. Keeping your data current helps retailers discover your products and
									reach out directly when they’re interested.
								</p>
							)
						},
						{
							id: 15,
							question:
								'Who has access to the Beacon Hub and what if I don’t see it in my profile?',
							answer: (
								<p>
									The person whose name and email are associated with your New Hope Standards
									application has access. Create an account on{' '}
									<a href='https://beacon.newhope.com' target='_blank' rel='noreferrer'>
										beacon.newhope.com
									</a>{' '}
									using that same email, and you’ll see “My Beacon Hub” in your profile. If you
									still don’t have access, please reach out to{' '}
									<a href='mailto:AudienceSuccessTeam@NewHope.com' target='_blank' rel='noreferrer'>
										AudienceSuccessTeam@NewHope.com
									</a>
									.
								</p>
							)
						},
						{
							id: 16,
							question: 'Some fields are not editable in the Beacon Hub, how can I update them?',
							answer: (
								<p>
									Certain fields in the Brand and Product Info sections are not yet editable by
									users. We’re working toward full self‑service editing, but in the meantime, to
									make additional changes please email{' '}
									<a href='mailto:AudienceSuccessTeam@NewHope.com' target='_blank' rel='noreferrer'>
										AudienceSuccessTeam@NewHope.com
									</a>
									.
								</p>
							)
						},
						{
							id: 17,
							question: 'What is a HowGood estimate and why should I show it on my products?',
							answer: (
								<>
									<p>
										As a Beacon brand, you automatically receive an affordable sustainability impact
										estimate from HowGood, giving your products transparency that retailers and
										consumers demand. This can be a major differentiator when buyers choose what to
										stock.
									</p>
									<p>
										HowGood is an independent research company with the world’s largest food product
										sustainability database. Drawing from over 600 data sources and certifications,
										they help leading brands and retailers measure and improve environmental and
										social impact. Note: estimates currently cover only food and beverage products.
									</p>
									<p>
										The HowGood Sustainability Impact Estimate measures overall product
										sustainability across eight key metrics: Greenhouse Gas Emissions, Soil Health,
										Water Usage, Land Use, Biodiversity, Animal Welfare, Labor Risk, and Processing.
										Scores on Beacon range from negative to positive impact:
									</p>
									<p>
										<strong>Good</strong> = better than 70% of assessed products
										<br />
										<strong>Great</strong> = better than 85%
										<br />
										<strong>Best</strong> = better than 95%
									</p>
									<p>
										<a href='https://www.howgood.com/new-hope' target='_blank' rel='noreferrer'>
											Learn more here
										</a>
										.
									</p>
								</>
							)
						}
						// section commented because of ticket BP-1891-hide question Violation and improvement
						// {
						// 	id: 18,
						// 	question:
						// 		'In the Product Info section, what is the difference between a Violation and an Improvement?',
						// 	answer: (
						// 		<>
						// 			<p>
						// 				A <strong>Violation</strong> indicates corrective action is required for a
						// 				product to be eligible on Beacon or for an upcoming Expo. An{' '}
						// 				<strong>Improvement</strong> is a suggestion from our Standards Specialists to
						// 				enhance compliance; it won’t prevent your product from displaying on Beacon or
						// 				at Expo.
						// 			</p>
						// 			<p>
						// 				To provide additional information or ask questions, contact your Standards
						// 				Specialist at{' '}
						// 				<a href='mailto:standards@newhope.com' target='_blank' rel='noreferrer'>
						// 					standards@newhope.com
						// 				</a>
						// 				. If you’d like full compliance assistance, visit{' '}
						// 				<a href='https://marketreadyinsights.com/' target='_blank' rel='noreferrer'>
						// 					MarketReady Insights
						// 				</a>
						// 				.
						// 			</p>
						// 		</>
						// 	)
						// }
					],
					id: 'beacon-hub-faq'
				}
		  ];
	const deviceScreen = useSelector((state: RootState) => state.appState?.deviceScreen);
	const [FAQSelected, setFAQSelected] = useState<IFAQSelected[]>([]);
	const [isDeskTopScreen, setIsDeskTopScreen] = useState<boolean>(
		deviceScreen === ScreenType.LARGE_DESKTOP ||
			deviceScreen === ScreenType.MEDIUM_DESKTOP ||
			deviceScreen === ScreenType.SMALL_DESKTOP
	);
	useEffect(() => {
		setIsDeskTopScreen(
			deviceScreen === ScreenType.LARGE_DESKTOP ||
				deviceScreen === ScreenType.MEDIUM_DESKTOP ||
				deviceScreen === ScreenType.SMALL_DESKTOP
		);
	}, [deviceScreen]);
	const toggleOpen = (index: number) => {
		const newOpenStates = FAQSelected.map((state, indexState) =>
			index === indexState
				? {
						...state,
						collapsed: !state.collapsed
				  }
				: state
		);
		setFAQSelected(newOpenStates);
	};

	useEffect(() => {
		if (FAQsArray) {
			setFAQSelected(
				FAQsArray.map(() => ({
					collapsed: true
				}))
			);
		}
	}, []);
	const handleNavigateBack = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const location = useLocation();

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const section = queryParams.get('section');
		if (section) {
			setTimeout(() => {
				let element: HTMLElement = document.getElementById(section) as HTMLElement;
				if (element) {
					element.scrollIntoView({ block: 'start' });
				}
			}, 1000);
		} else {
			window.scrollTo(0, 0);
		}
	}, []);

	return (
		<>
			{isGeneral ? (
				<div className='title-faq-section-container'>
					<span className='title-faq-section'>Frequently Asked Questions</span>
					{isDeskTopScreen && (
						<GoBackButtonComponent
							text='Back'
							icon={<i className='fa ri-arrow-left-s-line' aria-hidden='true'></i>}
							handelNavigate={handleNavigateBack}
						></GoBackButtonComponent>
					)}
				</div>
			) : null}
			<div className={`faq-section ${isGeneral ? 'general-faq' : ''}`}>
				{FAQSelected.length > 0 &&
					FAQsArray.map((section, index) => (
						<div key={index} className='faq-section-container' id={section.id}>
							<TitleForms
								isVisible={FAQSelected[index].collapsed}
								titleText={section.sectionTitle}
								toggleVisibility={() => toggleOpen(index)}
								readOnly
								isForm={false}
							/>
							{FAQSelected[index].collapsed &&
								section.questions.map((question: any) => (
									<FAQElement
										answer={question.answer}
										question={question.question}
										key={question.id}
									></FAQElement>
								))}
						</div>
					))}
			</div>
		</>
	);
};

export default FAQComponent;
